import React from "react";
import BackButtom from "../../../components/backButton";
import {
  NbsIbscbsCombinationModel,
  NbsIbscbsListModel,
  NbsSearchResult,
} from "../../../../domain/models/nbs-ibscbs";
import * as S from "./styles";

type Props = {
  // Busca de NBS
  nbsSearch: string;
  nbsResults: NbsSearchResult[];
  nbsLoading: boolean;
  onNbsSearchChange: (search: string) => void;
  onSelectNbs: (nbs: NbsSearchResult) => void;
  // NBS selecionada
  selectedNbs: NbsSearchResult | null;
  nbsIbscbsData: NbsIbscbsListModel | null;
  combinationsLoading: boolean;
  onBack: () => void;
  // Ações
  onSetDefault: (id: number) => void;
  settingDefault: boolean;
};

const NbsIbscbsAdminPage: React.FC<Props> = ({
  nbsSearch,
  nbsResults,
  nbsLoading,
  onNbsSearchChange,
  onSelectNbs,
  selectedNbs,
  nbsIbscbsData,
  combinationsLoading,
  onBack,
  onSetDefault,
  settingDefault,
}) => {
  // --- Tela de combinações (NBS selecionada) ---
  if (selectedNbs) {
    const renderCombinations = (): JSX.Element => {
      if (combinationsLoading) {
        return <S.LoadingText>Carregando combinações...</S.LoadingText>;
      }

      if (!nbsIbscbsData) {
        return <S.LoadingText>Nenhuma combinação IBSCBS encontrada para esta NBS.</S.LoadingText>;
      }

      return (
        <S.SectionCard>
          <S.InfoText>
            <strong>{nbsIbscbsData.codigoNbs}</strong> — {nbsIbscbsData.nbsDescricao}
            <br />
            Item LC 116: <code>{nbsIbscbsData.complementaryLawItem}</code>
            &nbsp;|&nbsp;{nbsIbscbsData.totalCombinacoes} combinação(ões)
          </S.InfoText>

          <S.Table>
            <thead>
              <tr>
                <th>Cód. Operação</th>
                <th>CCT</th>
                <th>Status</th>
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              {nbsIbscbsData.combinacoes.map((combo: NbsIbscbsCombinationModel) => (
                <tr key={combo.id}>
                  <td><code>{combo.codigoOperacao}</code></td>
                  <td><code>{combo.cct}</code></td>
                  <td>
                    <S.Badge $active={combo.isDefault}>
                      {combo.isDefault ? "Default" : "—"}
                    </S.Badge>
                  </td>
                  <td>
                    <S.DefaultButton
                      $isDefault={combo.isDefault}
                      disabled={combo.isDefault || settingDefault}
                      onClick={() => onSetDefault(combo.id)}
                    >
                      {combo.isDefault ? "✓ Default" : "Definir como default"}
                    </S.DefaultButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </S.Table>
        </S.SectionCard>
      );
    };

    return (
      <S.Wrapper>
        <S.BackHeader>
          <S.BackButton type="button" onClick={onBack}>← Voltar</S.BackButton>
          <S.PageTitle>
            NBS {selectedNbs.codigoNbs}
          </S.PageTitle>
        </S.BackHeader>

        {renderCombinations()}
      </S.Wrapper>
    );
  }

  // --- Tela de busca ---
  const renderSearchResults = (): JSX.Element => {
    if (nbsLoading) {
      return <S.LoadingText>Buscando...</S.LoadingText>;
    }

    if (nbsResults.length > 0) {
      return (
        <S.NbsResultList>
          {nbsResults.map((nbs) => (
            <S.NbsResultItem key={nbs.id} onClick={() => onSelectNbs(nbs)}>
              <code>{nbs.codigoNbs}</code>
              <span>{nbs.description}</span>
            </S.NbsResultItem>
          ))}
        </S.NbsResultList>
      );
    }

    if (nbsSearch.length >= 2) {
      return <S.LoadingText>Nenhuma NBS encontrada.</S.LoadingText>;
    }

    return <S.LoadingText>Digite pelo menos 2 caracteres para buscar.</S.LoadingText>;
  };

  return (
    <S.Wrapper>
      <BackButtom text="NBS × IBSCBS — Gerenciar Defaults" />
      <S.InfoBox>
        Gerencia a correlação entre NBS e os dados de IBSCBS (código de operação + classificação tributária) usados na emissão de NFS-e Nacional. Permite alterar qual combinação é o default para cada NBS.
      </S.InfoBox>
      <S.CardWrapper>
        <S.SearchInput
          type="text"
          placeholder="Buscar NBS por código ou descrição..."
          value={nbsSearch}
          onChange={(e) => onNbsSearchChange(e.target.value)}
        />
      </S.CardWrapper>

      {renderSearchResults()}
    </S.Wrapper>
  );
};

export default NbsIbscbsAdminPage;
